import { IconArrowLeft, IconCaretDown, IconChevronLeft, IconChevronRight } from "@components/ui/Icon"
import { tv } from "@utils/tw"
import { ContentBlocks } from "eddev/blocks"
import { useAppData } from "eddev/hooks"
import { useRouter } from "eddev/routing"
import { AnimatePresence, LayoutGroup, motion, MotionProps } from "motion/react"
import { useState } from "react"

type MenuItem = {
  id: string
  label: string
  uri: string
  target?: string
  children?: MenuItem[]
}

export function MobileMenu() {
  const buttonBlocks = useAppData((data) => data.templateParts?.headerButtons?.contentBlocks)
  const [submenuIndex, setSubmenuIndex] = useState<number | null>(null)
  const menu = useAppData((data) => {
    const getItems = (parent: number | null): MenuItem[] =>
      data.mainMenu?.menuItems?.nodes
        .filter((item) => item.parentDatabaseId === parent)
        .map((item, i) => ({
          id: [parent, i, item.databaseId].join("_"),
          label: item.label!,
          uri: item.uri!,
          target: item.target!,
          children: getItems(item.databaseId)!,
        })) ?? []
    return getItems(0)
  })

  const section = menu[submenuIndex ?? -1]

  // const [activeIndex, setActiveIndex] = useState<number>(() => {
  //   return menu.findIndex((item) => item.uri === window.location.pathname)
  // })

  return (
    <LayoutGroup>
      <div className="absolute inset-0 overflow-hidden flex flex-col">
        {/* Page 1 */}
        <motion.div className="absolute inset-0 will-change-transform overflow-auto">
          <div className="relative h-full w-full flex flex-col">
            {/* Top-level menu items */}
            <motion.ul className="flex flex-col text-fg type-ui-l flex-1" layoutRoot>
              {menu.map((item, i) => {
                return <MenuItem key={i} item={item} onToggle={() => setSubmenuIndex(i)} />
              })}
            </motion.ul>

            {/* Buttons */}
            <div className="flex items-center justify-center gap-2 px-[--padding] py-6 [&_>*]:flex-1">
              <ContentBlocks blocks={buttonBlocks} />
            </div>
          </div>

          {/* Overlay */}
          <motion.div
            variants={{ enabled: { opacity: 1 }, disabled: { opacity: 0 } }}
            animate={section ? "enabled" : "disabled"}
            className="absolute inset-0 pointer-events-none bg-[rgba(0,0,0,0.5)]"
          ></motion.div>
        </motion.div>

        {/* Active submenu */}
        <AnimatePresence>
          {section && (
            <motion.div
              {...pageAnimation}
              initial="right"
              animate="center"
              exit="right"
              key={submenuIndex}
              className="absolute inset-0 will-change-transform bg-bg overflow-auto"
            >
              <motion.ul className="flex flex-col text-fg type-ui-l" layoutRoot>
                {/* Parent item */}
                <div className="sticky top-0">
                  <MenuItem
                    item={section}
                    onToggle={() => {
                      setSubmenuIndex(null)
                    }}
                    expanded
                  />
                </div>
                {/* Submenu items */}
                {section.children?.map((item, i) => {
                  return <MenuItem key={i} item={item} isChild />
                })}
              </motion.ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </LayoutGroup>
  )
}

const pageAnimation: MotionProps = {
  variants: {
    left: { x: "-100%", opacity: 1 },
    right: { x: "100%", opacity: 1 },
    center: { x: 0, opacity: 1 },
  },
  transition: {
    stiffness: 200,
    damping: 30,
    type: "spring",
    restDelta: 0.001,
  },
}

type MenuItemProps = {
  // id: string
  item: MenuItem
  expanded?: boolean
  isChild?: boolean
  onToggle?: () => void
}

function MenuItem(props: MenuItemProps) {
  const iconNode = (
    <motion.div key="icon" layout>
      {props.expanded ? <IconChevronLeft /> : <IconChevronRight />}
    </motion.div>
  )

  const isLink = props.item.uri !== "#"

  const router = useRouter()

  return (
    <motion.a
      href={props.item.uri}
      className="flex w-full px-[--padding] border-b border-t -mt-1 border-bg-low bg-bg text-fg text-left items-center py-[1em] cursor-pointer type-ul-l antialiased active:bg-bg-low gap-[0.5em]"
      onClick={(e) => {
        if (isLink) {
          router.handleClickEvent(e)
        } else if (props.onToggle) {
          e.preventDefault()
          return props.onToggle?.()
        }
      }}
    >
      {props.isChild ? (
        <span className="size-[24px]" />
      ) : props.expanded ? (
        <IconCaretDown className="size-[24px] rotate-90" />
      ) : null}
      <motion.div className="flex-1">{props.item.label}</motion.div>
      {!props.expanded && !isLink && <IconCaretDown className="size-[24px] -rotate-90" />}
    </motion.a>
  )
}

const linkStyles = tv({
  base: [
    "underline text-fg text-left",
    "underline-offset-[0.3em] decoration-[0.5px] decoration-transparent",
    "hover:decoration-fg-secondary/60 transition-colors",
  ],
  variants: {
    isLink: {
      true: "link-active:decoration-fg-secondary/60 link-child-active:decoration-fg-secondary/60",
    },
  },
  defaultVariants: {
    isLink: true,
  },
})
